import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Search, XCircle } from 'lucide-react';
import Button from '../ui/Button';
import cultivarClaimsService from '../../services/cultivarClaims';
import type {
  ClaimListState, CultivarClaim, LicensorClaim, RegistryCheckResult,
} from '../../services/cultivarClaims';
import { RightsBadge } from '../cultivar/RightsNotice';
import { ApiError } from '../../types';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import { cn } from '../../utils/cn';

/**
 * Super Admin → Claims. Every protected cultivar whose owner's claim to the
 * rights has not been checked, and every organisation that says it licenses
 * for a breeder not on the platform. FactreeTech decides each one (Rob,
 * 2026-10-12). Until then licences on it read as not verified; a rejected
 * cultivar takes no new licences.
 *
 * The server says what is pending and refuses what it must (a reason for a
 * rejection; one registration number verified for two organisations). This
 * page shows the evidence and records the decision.
 */

const STATES: { key: ClaimListState; label: string }[] = [
  { key: 'pending', label: 'Waiting' },
  { key: 'verified', label: 'Verified' },
  { key: 'rejected', label: 'Rejected' },
];

function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}

function RegistryResults({ results }: { results: RegistryCheckResult[] }) {
  if (results.length === 0) {
    return <p className="text-sm text-neutral-600">It claims no registration numbers, so there is nothing to look up.</p>;
  }
  return (
    <ul className="space-y-2">
      {results.map((r, i) => (
        <li key={i} className={cn(
          'rounded border p-2 text-sm break-words',
          r.found && r.name_matches ? 'border-emerald-200 bg-emerald-50' :
          r.found ? 'border-amber-200 bg-amber-50' :
          r.checked ? 'border-red-200 bg-red-50' : 'border-neutral-200 bg-neutral-50',
        )}>
          <p className="font-medium">{r.jurisdiction} {r.type.toUpperCase()} {r.registration_number || '(no number)'}</p>
          {r.found ? (
            <div className="text-neutral-800">
              <p>Registered variety: <strong>{r.denomination || 'not given'}</strong>
                {r.name_matches === false && ' — not this cultivar\'s name'}
                {r.name_matches === true && ' — matches'}
              </p>
              <p>Applicant: {r.applicant || 'not given (individuals are anonymised)'}</p>
              {r.registry_status && <p>Status: {r.registry_status}</p>}
              {r.dataset && <p className="text-xs text-neutral-500">From {r.dataset}</p>}
            </div>
          ) : (
            <p className="text-neutral-700">{r.message}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

function ClaimCard({
  claim, tz, selectable, selected, onToggle, onDecide, busy,
}: {
  claim: CultivarClaim;
  tz: string;
  selectable: boolean;
  selected: boolean;
  onToggle: () => void;
  onDecide: (ids: number[], decision: 'verified' | 'rejected') => void;
  busy: boolean;
}) {
  const [checking, setChecking] = useState(false);
  const [registry, setRegistry] = useState<RegistryCheckResult[] | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  const check = async () => {
    setChecking(true);
    setCheckError(null);
    try {
      const res = await cultivarClaimsService.registryCheck(claim.id);
      if (res.success) setRegistry(res.data.results);
      else setCheckError(res.error.message);
    } catch (e) {
      setCheckError(errorText(e));
    } finally {
      setChecking(false);
    }
  };

  const breeder = claim.breeder_org_name || claim.breeder_name;
  const clashes = claim.same_number_elsewhere || [];

  return (
    <div className={cn('rounded-xl border bg-white p-4 space-y-3 min-w-0', selected ? 'border-primary-400 ring-1 ring-primary-300' : 'border-neutral-200')}>
      <div className="flex items-start gap-3 min-w-0">
        {selectable && (
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0"
            checked={selected}
            onChange={onToggle}
            aria-label={`Select ${claim.name}`}
          />
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-neutral-900 break-words">
              {claim.name}{claim.trade_name ? ` (${claim.trade_name})` : ''}
            </h3>
            <RightsBadge state={claim.rights_state} />
          </div>
          <p className="text-sm text-neutral-700 break-words">
            Claimed by <strong>{claim.org_name || 'an unknown organisation'}</strong>
            {claim.crop_type_name ? ` · ${claim.crop_type_name}` : ''}
            {` · ${claim.protection_status === 'pending' ? 'protection applied for' : 'protected'}`}
          </p>
          <p className="text-sm text-neutral-700 break-words">
            Breeder: {breeder || <span className="text-amber-700">not named</span>}
            {claim.breeder_claim_state === 'unclaimed' && ' (not on the platform)'}
          </p>
        </div>
      </div>

      <div className="text-sm space-y-1">
        <p className="text-neutral-500">Registrations claimed</p>
        {claim.protections.length === 0 ? (
          <p className="text-amber-800">None. It says it is protected but gives no registration.</p>
        ) : (
          <ul className="space-y-0.5">
            {claim.protections.map(p => (
              <li key={p.id} className="break-words">
                {p.jurisdiction} {p.type.toUpperCase()} <span className="font-mono">{p.registration_number || '(no number)'}</span>
                {` · ${p.status.replace(/_/g, ' ')}`}
                {p.authority ? ` · ${p.authority}` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-sm text-neutral-700 break-words">
        {claim.live_licences} live licence{claim.live_licences === 1 ? '' : 's'}
        {claim.licensor_names.length > 0 && ` · Licensors: ${claim.licensor_names.join(', ')}`}
      </p>

      {clashes.length > 0 && (
        <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-medium">The same registration number is claimed elsewhere</p>
            <ul>
              {clashes.map(c => (
                <li key={`${c.cultivar_id}-${c.registration_number}`} className="break-words">
                  {c.registration_number}: {c.name} ({c.org_name || 'unknown organisation'}) — {c.rights_state.replace('_', ' ')}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {claim.rights_reviewed_at && claim.rights_state !== 'pending' && (
        <p className="text-sm text-neutral-600 break-words">
          {claim.rights_state === 'verified' ? 'Verified' : 'Rejected'} {formatDateInTz(claim.rights_reviewed_at, tz)}
          {claim.reviewed_by_name ? ` by ${claim.reviewed_by_name}` : ''}
          {claim.rights_review_note ? `: ${claim.rights_review_note}` : ''}
        </p>
      )}

      {registry && <RegistryResults results={registry} />}
      {checkError && <p className="text-sm text-red-700">{checkError}</p>}

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={check} disabled={checking}>
          {checking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
          Check IP Australia
        </Button>
        {claim.rights_state !== 'verified' && (
          <Button size="sm" onClick={() => onDecide([claim.id], 'verified')} disabled={busy}>
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Verify
          </Button>
        )}
        {claim.rights_state !== 'rejected' && (
          <Button variant="danger" size="sm" onClick={() => onDecide([claim.id], 'rejected')} disabled={busy}>
            <XCircle className="h-4 w-4 mr-1.5" /> Reject
          </Button>
        )}
      </div>
    </div>
  );
}

function LicensorCard({
  claim, tz, onDecide, busy,
}: {
  claim: LicensorClaim;
  tz: string;
  onDecide: (ids: number[], decision: 'verified' | 'rejected') => void;
  busy: boolean;
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 space-y-2 min-w-0">
      <p className="text-sm text-neutral-900 break-words">
        <strong>{claim.licensor_org_name}</strong> says it licenses for <strong>{claim.breeder_org_name}</strong> in {claim.region_code}
      </p>
      <p className="text-sm text-neutral-700 break-words">
        {claim.scope === 'all_cultivars' ? 'All of the breeder\'s cultivars' : `Cultivars: ${claim.cultivar_names.join(', ') || 'none named'}`}
        {` · claimed ${formatDateInTz(claim.claimed_at, tz)}`}
        {claim.claimed_by_name ? ` by ${claim.claimed_by_name}` : ''}
      </p>
      <p className="text-sm text-neutral-700 break-words">
        Breeder {claim.breeder_claim_state === 'unclaimed' ? 'is not on the platform' : 'is on the platform'}
        {claim.breeder_country ? ` · ${claim.breeder_country}` : ''}
        {claim.breeder_website ? ` · ${claim.breeder_website}` : ''}
      </p>
      {claim.notes && <p className="text-sm text-neutral-600 break-words">Their note: {claim.notes}</p>}
      {claim.platform_review_note && (
        <p className="text-sm text-neutral-600 break-words">Note from FactreeTech: {claim.platform_review_note}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {claim.platform_review_state !== 'verified' && (
          <Button size="sm" onClick={() => onDecide([claim.id], 'verified')} disabled={busy}>
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Verify
          </Button>
        )}
        {claim.platform_review_state !== 'rejected' && (
          <Button variant="danger" size="sm" onClick={() => onDecide([claim.id], 'rejected')} disabled={busy}>
            <XCircle className="h-4 w-4 mr-1.5" /> Reject
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CultivarClaimsPanel({ onChanged }: { onChanged?: () => void }) {
  const tz = useOrgTimezone();
  const [state, setState] = useState<ClaimListState>('pending');
  const [claims, setClaims] = useState<CultivarClaim[] | null>(null);
  const [licensors, setLicensors] = useState<LicensorClaim[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  // A rejection needs a reason, so it opens this form instead of going straight away.
  const [rejecting, setRejecting] = useState<{ kind: 'cultivar' | 'licensor'; ids: number[] } | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [c, l] = await Promise.all([
        cultivarClaimsService.list(state),
        cultivarClaimsService.listLicensors(state),
      ]);
      if (c.success) setClaims(c.data); else setError(c.error.message);
      if (l.success) setLicensors(l.data); else setError(l.error.message);
    } catch (e) {
      setError(errorText(e));
    }
  }, [state]);

  useEffect(() => { setClaims(null); setLicensors(null); setSelected(new Set()); load(); }, [load]);

  const byOrg = useMemo(() => {
    const groups = new Map<string, CultivarClaim[]>();
    for (const c of claims || []) {
      const key = c.org_name || 'Unknown organisation';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    }
    return [...groups.entries()];
  }, [claims]);

  const toggle = (id: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleGroup = (ids: number[]) => setSelected(prev => {
    const next = new Set(prev);
    const all = ids.every(id => next.has(id));
    ids.forEach(id => (all ? next.delete(id) : next.add(id)));
    return next;
  });

  const decide = async (kind: 'cultivar' | 'licensor', ids: number[], decision: 'verified' | 'rejected', why?: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      if (kind === 'cultivar') {
        const res = await cultivarClaimsService.decide(ids, decision, why);
        if (!res.success) { setError(res.error.message); return; }
        const done = res.data.decided.length;
        const refused = res.data.refused;
        setMessage(
          `${done} ${decision === 'verified' ? 'verified' : 'rejected'}.` +
          (refused.length ? ` Not done: ${refused.map(r => `${r.name || `#${r.id}`} (${r.reason})`).join('; ')}` : '')
        );
      } else {
        const res = await cultivarClaimsService.decideLicensors(ids, decision, why);
        if (!res.success) { setError(res.error.message); return; }
        setMessage(`${res.data.decided.length} ${decision === 'verified' ? 'verified' : 'rejected'}.`);
      }
      setSelected(new Set());
      setRejecting(null);
      setNote('');
      await load();
      onChanged?.();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const onDecideCultivar = (ids: number[], decision: 'verified' | 'rejected') => {
    if (decision === 'rejected') { setRejecting({ kind: 'cultivar', ids }); setNote(''); return; }
    decide('cultivar', ids, decision);
  };
  const onDecideLicensor = (ids: number[], decision: 'verified' | 'rejected') => {
    if (decision === 'rejected') { setRejecting({ kind: 'licensor', ids }); setNote(''); return; }
    decide('licensor', ids, decision);
  };

  const loading = claims === null || licensors === null;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700 space-y-1">
        <p>
          A protected cultivar reads as <strong>not yet verified</strong> on its page, its licences and the public
          verify page until it is verified here. A rejected cultivar takes no new licences; licences already issued
          stay, and show it. Changing its owner, breeder or a registration number after a decision sends it back here.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATES.map(s => (
          <button
            key={s.key}
            onClick={() => setState(s.key)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
              state === s.key
                ? 'bg-neutral-900 text-white border-neutral-900'
                : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-400'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 break-words">{error}</div>}
      {message && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 break-words">{message}</div>}

      {rejecting && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-2">
          <label htmlFor="claim-reject-note" className="block text-sm font-semibold text-red-900">
            Why {rejecting.ids.length === 1 ? 'is this claim' : `are these ${rejecting.ids.length} claims`} rejected?
          </label>
          <p className="text-sm text-red-800">The organisation is emailed this reason.</p>
          <textarea
            id="claim-reject-note"
            className="w-full rounded border border-red-300 p-2 text-sm"
            rows={3}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="danger"
              size="sm"
              disabled={busy || !note.trim()}
              onClick={() => decide(rejecting.kind, rejecting.ids, 'rejected', note.trim())}
            >
              Reject
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setRejecting(null)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>
      ) : (
        <>
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-neutral-900">Protected cultivars ({claims!.length})</h3>
              {state === 'pending' && selected.size > 0 && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => decide('cultivar', [...selected], 'verified')} disabled={busy}>
                    Verify {selected.size}
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => onDecideCultivar([...selected], 'rejected')} disabled={busy}>
                    Reject {selected.size}
                  </Button>
                </div>
              )}
            </div>
            {claims!.length === 0 && (
              <p className="text-sm text-neutral-500">
                {state === 'pending' ? 'Nothing waiting. Every protected cultivar has been decided.' : 'None.'}
              </p>
            )}
            {byOrg.map(([org, list]) => (
              <div key={org} className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-neutral-700 break-words">{org} · {list.length}</p>
                  {state === 'pending' && list.length > 1 && (
                    <button className="text-sm text-primary-700 hover:underline" onClick={() => toggleGroup(list.map(c => c.id))}>
                      {list.every(c => selected.has(c.id)) ? 'Clear these' : `Select all ${list.length}`}
                    </button>
                  )}
                </div>
                {list.map(c => (
                  <ClaimCard
                    key={c.id}
                    claim={c}
                    tz={tz}
                    selectable={state === 'pending'}
                    selected={selected.has(c.id)}
                    onToggle={() => toggle(c.id)}
                    onDecide={onDecideCultivar}
                    busy={busy}
                  />
                ))}
              </div>
            ))}
          </section>

          <section className="space-y-3">
            <h3 className="text-base font-semibold text-neutral-900">Licensor claims ({licensors!.length})</h3>
            <p className="text-sm text-neutral-600">
              An organisation saying it licenses for a breeder that is not on the platform. Until verified it cannot see
              that breeder's plantings map.
            </p>
            {licensors!.length === 0 && <p className="text-sm text-neutral-500">None.</p>}
            {licensors!.map(l => (
              <LicensorCard key={l.id} claim={l} tz={tz} onDecide={onDecideLicensor} busy={busy} />
            ))}
          </section>
        </>
      )}
    </div>
  );
}
